import * as ids from 'short-id';
import { IVideoUpload } from 'redux/middleware/api/media-upload-service';
import { CropMetadata, Dimensions, FitType, IVideoClip, Size } from 'types';
import { Pixels, ViewportHeight, ViewportWidth } from 'utils/measurement';
import {
  fitElement,
  measurementToPx,
  measurementToViewport,
  scale,
  viewportToPct,
} from 'utils/placement';
import { MediaIntegrationId, VideoIntegrationData } from '../../../../types';
import {
  createNewLayer,
  deleteLayer,
  getRemainingAssetCountInLayer,
} from '../../shared/layers';
import { updateState } from '../../shared/mutations';
import { getUpdatedPlacement } from '../../shared/placement';
import {
  FramingMethod,
  VideoClip,
  VideoClipState,
  VideoTemplateStateContent,
} from '../../types';
import { deleteImage } from '../slideshow/mutations';
import { AddMediaMetaData, AddVideoClipOptions } from './types';
import { getVideoPlacementDimensions } from './utils';

const getVideoUrlFromUpload = (src: IVideoUpload): string => {
  const webmVariation = src.variations?.find(
    variation => variation.type === 'transparent' && variation.isTransparent,
  );
  return webmVariation ? webmVariation.url : src.transcodedVideoUrl;
};

const VIDEO_MEDIA_INTEGRATION_TO_ASSET_TYPE_MAP = {
  [MediaIntegrationId.GIF]: 'gif',
  [MediaIntegrationId.VIDEOCLIP]: 'video',
} as const;

/**
 * Generates the base config for an UCS videoclip from its source, integration data (videoclip of gif) and the layer index.
 * @param {object} src - video upload response output
 * @param {object} integrationData - object containing the type of upload (videoclip or gif)
 * @param {number} layerIndex - layer index for the layer to which this clip is goin to be assigned
 * @returns {object} - a video clip object generated from the uploaded gif/video data.
 */
const createVideoClipFromVideoUpload = (
  src: IVideoUpload,
  integrationData: VideoIntegrationData,
  initialPlacement: Dimensions<
    ViewportHeight,
    ViewportWidth,
    ViewportHeight,
    ViewportWidth
  >,
  trimStartMillis?: number,
  trimEndMillis?: number,
): IVideoClip => {
  const placement = viewportToPct(initialPlacement);
  return {
    assetType: VIDEO_MEDIA_INTEGRATION_TO_ASSET_TYPE_MAP[integrationData.id],
    // this layer id should be set when exporting as doing it while adding implies updating
    // it each time a layer is added/removed
    layerId: -1,
    // as there are no current controls for the track positioning initial offset and start
    // milli are set to 0.
    playOffsetMilli: 0,
    position: {
      top: placement.top,
      left: placement.left,
    },
    sourceDurationMilli: src.durationMillis,
    startMilli: 0,
    style: {
      height: placement.height,
      width: placement.width,
    },
    videoUrl: getVideoUrlFromUpload(src),
    videoId: src.id,
    editor: {
      videoHeight: src.videoHeight,
      videoWidth: src.videoWidth,
      viewport: undefined,
      zoom: undefined,
    },
    previewThumbnail: {
      url: src.previewThumbnail.thumbnails?.[0]?.url,
    },
    ...(trimStartMillis !== undefined && { trimStartMillis }),
    ...(trimEndMillis !== undefined && { trimEndMillis }),
  };
};

/**
 * Adds a new videoclip element to videoclips collection. It generates a new videoclip
 * based on an uploaded video element and attaches it to a layer.
 * @param {object} state - current template editor state
 * @param {object} src - new videoclip element to add upload data obtained from be from the upload operation
 * @param {object} options - filename, id and integration data of the new videoclip element
 * @param {object} metadata - placement and targetLayerId. Both are optional. placement will be used as an initial placemente whereas targetLayerId will be used to place the new asset at an existing free layer instead of using a new one.
 * @returns {object} - updated template editor state.
 */
export function addVideo(
  state: VideoTemplateStateContent,
  src: IVideoUpload,
  options: AddVideoClipOptions = {},
  metadata: AddMediaMetaData = {},
): VideoTemplateStateContent {
  const { canvas } = state;
  const {
    fileName,
    id = ids.generate(),
    integrationData,
    enableBlur,
    fitType = 'fit',
    framingMethod,
    trimStartMillis,
    trimEndMillis,
  } = options;
  const { placement, targetLayerId, targetLayerIndex } = metadata;

  const [layerId, layers] = createNewLayer(
    state,
    'media',
    targetLayerId,
    targetLayerIndex,
  );

  const canvasPx = {
    height: new Pixels(canvas.height),
    width: new Pixels(canvas.width),
  };

  const videoSize: Size<Pixels> = {
    height: new Pixels(src.videoHeight),
    width: new Pixels(src.videoWidth),
  };

  // If an initial placement is provided it will be used. Otherwise, element will be fitted
  // in canvas in order to calculate its initial placement.
  const initialPlacement =
    placement ||
    measurementToViewport(fitElement(videoSize, canvasPx, fitType), canvas);

  // Videoclip base data is created. This object contains the information that will be sent as
  // config to the BE
  const originalVideoClip = createVideoClipFromVideoUpload(
    src,
    integrationData,
    initialPlacement,
    trimStartMillis,
    trimEndMillis,
  );

  const newVideoClip: Omit<VideoClip, 'position' | 'style'> = {
    fileName,
    integrationData,
    id,
    layerId,
    original: originalVideoClip,
    placement: initialPlacement,
    videoUrl: getVideoUrlFromUpload(src),
    enableBlur,
    framingMethod,
    fitType,
  };

  return updateState(state, {
    layers,
    videoClips: {
      order: [...state.videoClips.order, id],
      data: {
        ...state.videoClips.data,
        [id]: newVideoClip,
      },
    },
  });
}

/**
 * Deletes a videoclip asset from the template editor's state.
 * @param {object} state - Current template editor's state.
 * @param {string} id - Target videoclip to delete id.
 * @param {boolean} [keepLayer=false] - Wether to keep layer or not. This is useful when replacing an element because it will keep the layer and will allow to place a new asset on it.
 * @returns {object} - Updated template editor's state.
 */
export const deleteVideo = (
  state: VideoTemplateStateContent,
  id: string,
  keepLayer = false,
): VideoTemplateStateContent => {
  const { videoClips } = state;
  const videoClipAsset = videoClips.data[id];
  const { layerId } = videoClipAsset;

  // Counts slideshow and videoclips remaining layers, updates the videoclips order
  // and finally it removes the videoclip from data
  const nAssetsLeftInLayer = getRemainingAssetCountInLayer(state, layerId);
  const updatedOrder = state.videoClips.order?.filter(currId => currId !== id);
  const updatedClipsData = updatedOrder.reduce((acc, currKey) => {
    return { ...acc, [currKey]: state.videoClips.data[currKey] };
  }, {} as VideoClipState['data']);

  return {
    ...state,
    ...(nAssetsLeftInLayer === 0 && !keepLayer
      ? deleteLayer(state, layerId)
      : undefined),
    videoClips: {
      order: updatedOrder,
      data: updatedClipsData,
    },
  };
};

/**
 * Replaces a video/gif asset with a different video/gif or image one.
 * @param {object} state - Current template editor's state.
 * @param {string} id - Target asset to replace id.
 * @param {object} src - New videoclip element to add upload data obtained from be from the upload operation
 * @param {string} previewType - The type of the new asset that is going to be replace (image, videoClip or none)
 * @param {object} param4 - New asset options: fileName, integrationData, metadata and original element
 * @returns {object} - Updated template editor's state.
 */
export function replaceVideo(
  state: VideoTemplateStateContent,
  id: string,
  src: IVideoUpload,
  previewType: 'image' | 'none' | 'videoClip',
  {
    fileName,
    integrationData,
    enableBlur,
    framingMethod,
    fitType,
    trimStartMillis,
    trimEndMillis,
    metadata,
    original,
  }: Omit<AddVideoClipOptions, 'id'>,
): VideoTemplateStateContent {
  if (previewType === 'none') {
    return state;
  }

  const { slideshow, videoClips } = state;
  const currVideoClip = videoClips.data[id];
  const currVideoClipEnableBlur = currVideoClip?.original?.backfill?.enableBlur;
  const shouldEnableBlur = enableBlur ?? currVideoClipEnableBlur;

  // This new condition allows the possibility of replacing an image with a video/gif
  // asset. When this case is matched, target layer id, then image asset is removed
  // but its layer kept and new video/gif is added using the target layer in order to
  // avoid adding it in a new layer.
  if (previewType === 'image') {
    const slide = slideshow.data[id];
    const targetLayerId = slide?.layerId;
    const newAssetDimensions = {
      height: src.videoHeight,
      width: src.videoWidth,
    };
    const placement = getUpdatedPlacement(
      state,
      slide.placement,
      newAssetDimensions,
    );
    return addVideo(
      deleteImage(state, id, true),
      src,
      {
        fileName,
        id,
        original,
        metadata,
        integrationData,
        enableBlur: shouldEnableBlur,
        framingMethod,
        fitType,
        trimStartMillis,
        trimEndMillis,
      },
      { placement, targetLayerId },
    );
  }

  const newAssetDimensions = {
    height: src.videoHeight,
    width: src.videoWidth,
  };
  const fittedPlacement = getUpdatedPlacement(
    state,
    getVideoPlacementDimensions(currVideoClip),
    newAssetDimensions,
  );
  const originalVideoClip = createVideoClipFromVideoUpload(
    src,
    integrationData,
    fittedPlacement,
  );

  const newVideoClip: Omit<VideoClip, 'position' | 'style'> = {
    fileName,
    integrationData,
    enableBlur: shouldEnableBlur,
    id,
    layerId: currVideoClip?.layerId,
    original: originalVideoClip,
    placement: fittedPlacement,
    videoUrl: getVideoUrlFromUpload(src),
    metadata: undefined,
  };

  return updateState(state, {
    videoClips: {
      data: {
        ...state.videoClips.data,
        [id]: newVideoClip,
      },
    },
  });
}

export function recropVideoClip(
  state: VideoTemplateStateContent,
  id: string,
  metadata: CropMetadata,
): VideoTemplateStateContent {
  const { videoClips } = state;

  return {
    ...state,
    videoClips: {
      ...videoClips,
      data: {
        ...videoClips.data,
        [id]: {
          ...videoClips.data[id],
          metadata,
        },
      },
    },
  };
}

/**
 * Scales a video/gif asset to respond to the will zoom event
 * @param {object} state - Current template editor's state.
 * @param {string} id - Target asset to replace id.
 * @param {number} multiplier - Scaling multiplier factor.
 * @returns {object} - Updated template editor's state.
 */
export function scaleVideoClip(
  state: VideoTemplateStateContent,
  id: string,
  multiplier: number,
): VideoTemplateStateContent {
  const { canvas, videoClips } = state;
  const videoClip = videoClips.data[id];

  return updateState(state, {
    videoClips: {
      data: {
        [id]: {
          placement: measurementToViewport(
            scale(measurementToPx(videoClip.placement, canvas), multiplier, {
              minArea: 500,
            }),
            canvas,
          ),
        },
      },
    },
  });
}

/**
 * Updates a video/gif asset placement
 * @param {object} state - Current template editor's state.
 * @param {string} id - Target asset to replace id.
 * @param {object} placement - New placement object.
 * @returns {object} - Updated template editor's state.
 */
export function updateVideoClipPlacement(
  state: VideoTemplateStateContent,
  id: string,
  placement: Dimensions<Pixels>,
  fitType?: FitType,
): VideoTemplateStateContent {
  const { canvas } = state;

  const updatedPlacement = measurementToViewport(
    {
      height: placement.height,
      left: placement.left,
      top: placement.top,
      width: placement.width,
    },
    canvas,
  );

  return updateState(state, {
    videoClips: {
      data: {
        [id]: {
          placement: updatedPlacement,
          fitType,
        },
      },
    },
  });
}

export function updateVideoBlur(
  state: VideoTemplateStateContent,
  id: string,
  enableBlur: boolean,
) {
  return updateState(state, {
    videoClips: {
      data: {
        [id]: {
          enableBlur,
        },
      },
    },
  });
}

export function updateVideoFramingMethod(
  state: VideoTemplateStateContent,
  id: string,
  framingMethod: FramingMethod,
) {
  return updateState(state, {
    videoClips: {
      data: {
        [id]: {
          framingMethod,
        },
      },
    },
  });
}

/**
 * Sets the initial placement for a particular video/gif element.
 * Updates a video/gif asset placement
 * @param {object} state - Current template editor's state.
 * @param {string} id - Target asset to replace id.
 * @param {object} position - Initial top and left position.
 * @param {object} size - Initial height and width.
 * @returns {object} - Updated template editor's state.
 */
export const setInitialVideoClipPlacement = (
  state: VideoTemplateStateContent,
  id: string,
  position: { top: number; left: number },
  size: { height: number; width: number },
) => {
  const { canvas, videoClips } = state;

  if (!videoClips.data[id]) {
    return state;
  }

  const placement = measurementToViewport(
    {
      ...{ top: new Pixels(position.top), left: new Pixels(position.left) },
      ...{ height: new Pixels(size.height), width: new Pixels(size.width) },
    },
    canvas,
  );

  return updateState(state, {
    videoClips: {
      data: {
        [id]: {
          placement,
        },
      },
    },
  });
};
